import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { postApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Building2, DollarSign, Users, MapPin,
  TrendingUp, CreditCard, CheckCircle2, Loader2, ArrowLeft
} from 'lucide-react';

// ── Price IDs by role ────────────────────────────────────────────────────────
const PRICE_CONFIG: Record<string, {
  priceId: string;
  amount: number;
  labelEn: string;
  labelFr: string;
  planId: string;
}> = {
 seller: {
  priceId: 'price_1Tvp3s2RIMiIVSovl1B1Osfh',  //à remplacer par le vrai priceId Stripe pour le rôle "seller"
  amount: 300,
  labelEn: 'Deal Pre-Evaluation Fee',
  labelFr: 'Frais de pré-évaluation du deal',
  planId: 'seller_pre_evaluation',
},
broker: {
  priceId: 'price_1Tvp3s2RIMiIVSovl1B1Osfh',  //à remplacer par le vrai priceId Stripe pour le rôle "broker"
  amount: 300,
  labelEn: 'Deal Pre-Evaluation Fee',
  labelFr: 'Frais de pré-évaluation du deal',
  planId: 'broker_pre_evaluation',
},
  buyer_byod: {
    priceId: 'price_1Td0YPCkUJGKtqQAf0h1sMue',
    amount: 499,
    labelEn: 'BYOD Deal Submission',
    labelFr: 'Soumission BYOD',
    planId: 'buyer_byod',
  },
  buyer_byod_essential: {
    priceId: 'price_1Td1EACkUJGKtqQA7Da7irts',
    amount: 399,
    labelEn: 'BYOD Essential — Deal Submission',
    labelFr: 'BYOD Essential — Soumission de deal',
    planId: 'buyer_byod_essential',
  },
  buyer_byod_essential_plus: {
    priceId: 'price_1Td1EvCkUJGKtqQAgmrfvLdl',
    amount: 299,
    labelEn: 'BYOD Essential Plus — Deal Submission',
    labelFr: 'BYOD Essential Plus — Soumission de deal',
    planId: 'buyer_byod_essential_plus',
  },
  buyer_byod_acquisition_pro: {
    priceId: 'price_1ThhOqCkUJGKtqQADRDHI5z8',
    amount: 199,
    labelEn: 'BYOD Acquisition Pro — Deal Submission',
    labelFr: 'BYOD Acquisition Pro — Soumission de deal',
    planId: 'buyer_byod_acquisition_pro',
  },
};

// Map subscriptionTier → priceConfig key
function getPriceKey(role: string, subscriptionTier?: string): string {
  if (role === 'seller') return 'seller';
  if (role === 'broker') return 'broker';
  if (role === 'buyer' || role === 'buyer_byod') {
    if (subscriptionTier === 'acquisition_pro') return 'buyer_byod_acquisition_pro';
    if (subscriptionTier === 'essential_plus')  return 'buyer_byod_essential_plus';
    if (subscriptionTier === 'essential')        return 'buyer_byod_essential';
    return 'buyer_byod'; // no subscription
  }
  return 'seller'; // fallback
}

// CORRIGÉ : détermine le rôle réel de l'utilisateur pour ce checkout.
// AVANT : `(user as any).role || 'seller'` lisait un champ singulier
// `role` qui n'existe pas sur la plupart des comptes broker (leur rôle
// est stocké dans le tableau `roles`, ex. `roles: ["broker"]`, dans
// userProfiles). Résultat : TOUT broker retombait silencieusement sur
// 'seller' — le deal était alors créé avec brokerId: null et
// sellerId = uid du broker lui-même (voir deals.service.ts
// submitDeal()), cassant complètement le lien Broker→Vendeur : le
// broker devenait "le vendeur" de son propre deal aux yeux du système.
//
// MAINTENANT : vérifie d'abord le tableau `roles` (source de vérité
// pour la plupart des comptes), avec priorité broker > seller > buyer
// > le champ singulier `role` en dernier recours (compat anciens
// comptes qui l'auraient encore) > 'seller' comme filet ultime.
function resolveUserRole(user: any): string {
  const roles: string[] = Array.isArray(user?.roles) ? user.roles : [];
  if (roles.includes('broker')) return 'broker';
  if (roles.includes('seller')) return 'seller';
  if (roles.includes('buyer'))  return 'buyer';
  return user?.role || 'seller';
}

// ── Translations ─────────────────────────────────────────────────────────────
const T = {
  en: {
    title: 'Finalize Your Submission',
    subtitle: 'Review your deal summary and complete payment to submit',
    dealSummary: 'Deal Summary',
    businessName: 'Business Name',
    industry: 'Industry',
    location: 'Location',
    askingPrice: 'Asking Price',
    employees: 'Employees',
    revenue: 'Revenue (Year 1)',
    ebitda: 'EBITDA (Year 1)',
    paymentDetails: 'Payment Details',
    fee: 'Submission Fee',
    total: 'Total Due Today',
    nextStep: 'After payment: Your deal will be reviewed within 2–3 business days.',
    pay: 'Pay & Submit Deal',
    back: 'Back to Form',
    processing: 'Redirecting to payment...',
    included: 'Included in your subscription',
    submitFree: 'Submit Deal (No Payment Required)',
    submitting: 'Submitting...',
    role: 'Submitted as',
    submissionFee: 'Submission type',
    includesRealEstate: '(includes real estate)',
  },
  fr: {
    title: 'Finaliser votre soumission',
    subtitle: 'Vérifiez le résumé de votre deal et complétez le paiement pour soumettre',
    dealSummary: 'Résumé du Deal',
    businessName: 'Nom de l\'entreprise',
    industry: 'Secteur',
    location: 'Localisation',
    askingPrice: 'Prix demandé',
    employees: 'Employés',
    revenue: 'Revenus (Année 1)',
    ebitda: 'EBITDA (Année 1)',
    paymentDetails: 'Détails du paiement',
    fee: 'Frais de soumission',
    total: 'Total à payer aujourd\'hui',
    nextStep: 'Après le paiement : votre deal sera examiné dans 2–3 jours ouvrables.',
    pay: 'Payer et soumettre le deal',
    back: 'Retour au formulaire',
    processing: 'Redirection vers le paiement...',
    included: 'Inclus dans votre abonnement',
    submitFree: 'Soumettre le deal (sans paiement)',
    submitting: 'Soumission en cours...',
    role: 'Soumis en tant que',
    submissionFee: 'Type de soumission',
    includesRealEstate: '(incluant immobilier)',
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);

const NAVY = '#1D314D';
const GOLD  = '#CEAB65';

function InfoRow({ label, value }: { label: string; value: string | number | undefined }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function DealCheckout() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = T[language as 'en' | 'fr'] ?? T.en;

  const [formData, setFormData] = useState<any>(null);
  const [userRole, setUserRole] = useState<string>('seller');
  const [subscriptionTier, setSubscriptionTier] = useState<string | undefined>();
  const [processing, setProcessing] = useState(false);
  const [submittingFree, setSubmittingFree] = useState(false);

  // Load saved form data from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('dealSubmissionDraft');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData(parsed.formData || parsed);
      } catch {
        navigate('/seller/submit-deal');
      }
    } else {
      navigate('/seller/submit-deal');
    }

    // Determine role — voir resolveUserRole() ci-dessus pour le détail
    // du correctif (lecture du tableau roles, pas du champ role seul).
    if (user) {
      const role = resolveUserRole(user);
      const tier  = (user as any).subscriptionTier;
      setUserRole(role);
      setSubscriptionTier(tier);
    }
  }, [user, navigate]);

  if (!formData) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: NAVY }} />
    </div>
  );

  const priceKey    = getPriceKey(userRole, subscriptionTier);
  const priceConfig = PRICE_CONFIG[priceKey];
  const isFree      = false; // All roles pay something per our structure
  const feeLabel    = language === 'fr' ? priceConfig.labelFr : priceConfig.labelEn;

  // Dashboard redirect after success based on role
  const getSuccessRedirect = () => {
    if (userRole === 'broker')  return '/dashboard/broker';
    if (userRole === 'buyer')   return '/dashboard/buyer';
    return '/dashboard/seller';
  };

  // ── Launch Stripe checkout ──────────────────────────────────────────────
  const handlePay = async () => {
    if (!user) return;
    setProcessing(true);

    try {
      // Récupérer le dealId + les documents déjà uploadés depuis le draft
      const existingDraft = localStorage.getItem('dealSubmissionDraft');
      const draftData = existingDraft ? JSON.parse(existingDraft) : {};
      const dealId = draftData?.dealId || draftData?.formData?.id || '';

      localStorage.setItem('dealSubmissionPending', JSON.stringify({
        formData,
        userRole,
        subscriptionTier,
        priceKey,
        planId: priceConfig.planId,
        dealId,
        successRedirect: getSuccessRedirect(),
        timestamp: Date.now(),
        // Préserver les documents uploadés (analyseur IA + uploads manuels)
        // afin que PaymentSuccess.tsx puisse les transmettre au backend
        uploadedFiles: draftData?.uploadedFiles || null,
        analyzedDocs: draftData?.analyzedDocs || [],
      }));

      const data = await postApi('/payments/checkout', {
        type: 'one_time',
        priceId: priceConfig.priceId,
        planId: priceConfig.planId,
        description: feeLabel,
        metadata: {
          dealSubmission: 'true',
          checkout_type: 'pre_evaluation',
          userRole,
          priceKey,
          deal_id: draftData?.dealId || draftData?.formData?.id || '',
          firebase_uid: user.id || '',
        },
        successUrl: `${window.location.origin}/payment-success?type=deal_submission&role=${userRole}&step=pre_evaluation`,
        cancelUrl: `${window.location.origin}/seller/deal-checkout?canceled=true`,
      });

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err: any) {
      toast({
        title: language === 'fr' ? 'Erreur' : 'Error',
        description: err?.message || (language === 'fr' ? 'Une erreur est survenue.' : 'An error occurred.'),
        variant: 'destructive',
      });
      setProcessing(false);
    }
  };

  const g = formData?.general || {};
  const ops = formData?.operations || {};
  const fin = formData?.financials || {};

  // CORRIGÉ : affiche le prix total (entreprise + immobilier) quand le
  // vendeur/broker a choisi d'inclure l'immobilier à l'étape 6
  // (StepFinancials.tsx écrit déjà ce total dans
  // totalAskingPriceWithRealEstate — ce composant l'ignorait auparavant
  // et affichait toujours le prix de l'entreprise seule).
  const displayAskingPrice = fin.includeRealEstateInPrice && fin.totalAskingPriceWithRealEstate
    ? fin.totalAskingPriceWithRealEstate
    : fin.askingPrice;
  const askingPriceLabel = fin.includeRealEstateInPrice
    ? `${fmt(displayAskingPrice)} ${t.includesRealEstate}`
    : (displayAskingPrice ? fmt(displayAskingPrice) : undefined);

  return (
    <div className="max-w-3xl mx-auto space-y-5 py-6 px-4">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/seller/submit-deal')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </button>
        <h1 className="text-2xl font-medium tracking-tight">{t.title}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t.subtitle}</p>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        {/* Deal Summary */}
        <Card style={{ border: '0.5px solid var(--color-border-tertiary)' }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-4 h-4" style={{ color: GOLD }} />
              {t.dealSummary}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <InfoRow label={t.businessName} value={g.legalName} />
            <InfoRow label={t.industry}     value={g.industry} />
            <InfoRow label={t.location}     value={[g.locationCity, g.locationCountry].filter(Boolean).join(', ')} />
            <InfoRow label={t.askingPrice}  value={askingPriceLabel} />
            <InfoRow label={t.employees}    value={ops.fullTimeEmployees} />
            <InfoRow label={t.revenue}      value={fin.revenueYear1 ? fmt(fin.revenueYear1) : undefined} />
            <InfoRow label={t.ebitda}       value={fin.ebitdaYear1 ? fmt(fin.ebitdaYear1) : undefined} />
            <InfoRow label={t.role}         value={userRole.charAt(0).toUpperCase() + userRole.slice(1)} />
          </CardContent>
        </Card>

        {/* Payment Details */}
        <Card style={{ border: '0.5px solid var(--color-border-tertiary)' }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="w-4 h-4" style={{ color: GOLD }} />
              {t.paymentDetails}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            {/* Fee breakdown */}
            <div>
              <div className="flex justify-between items-center py-2.5 border-b border-border">
                <span className="text-sm text-muted-foreground">{t.submissionFee}</span>
                <span className="text-sm font-medium">{feeLabel}</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-border">
                <span className="text-sm text-muted-foreground">{t.fee}</span>
                <span className="text-sm font-semibold" style={{ color: NAVY }}>
                  {fmt(priceConfig.amount)}
                </span>
              </div>
              <div
                className="flex justify-between items-center py-3 rounded-lg px-3 mt-2"
                style={{ background: 'rgba(29,49,77,0.05)', border: `0.5px solid rgba(29,49,77,0.1)` }}
              >
                <span className="text-sm font-semibold">{t.total}</span>
                <span className="text-lg font-bold" style={{ color: NAVY }}>
                  {fmt(priceConfig.amount)}
                </span>
              </div>
            </div>

            {/* Next steps note */}
            <div
              className="p-3 rounded-lg text-xs text-muted-foreground"
              style={{ background: 'rgba(206,171,101,0.08)', border: '0.5px solid rgba(206,171,101,0.2)' }}
            >
              <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5" style={{ color: GOLD }} />
              {t.nextStep}
            </div>

            {/* Pay button */}
            <Button
              className="w-full h-11 text-sm font-medium"
              onClick={handlePay}
              disabled={processing}
              style={{
                background: NAVY,
                color: GOLD,
                border: `1px solid rgba(206,171,101,0.3)`,
              }}
            >
              {processing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t.processing}</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" /> {t.pay}</>
              )}
            </Button>

            <p className="text-[10.5px] text-center text-muted-foreground">
              🔒 {language === 'fr' ? 'Paiement sécurisé via Stripe' : 'Secure payment via Stripe'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}